/* =========================================================================
 * 1. API-ответ “items / hasMore”
 * ========================================================================= */
export interface DataResponse<T> {
    items: T[];
    hasMore: boolean;
    total?: number;
}

/* =========================================================================
 * 2. Унифицированный результат после адаптера
 * ========================================================================= */
export interface InfiniteScrollResult<T> {
    items: T[];
    hasMore?: boolean;
    total?: number;
}

/* =========================================================================
 * 3. Адаптер: превращает «сырое» API-ответа R ➜ чистые данные T
 * ========================================================================= */
export interface ResponseAdapterParams<T, R> {
    response: R;
    currentItems: T[];
    page: number;
    pageSize: number;
}

export type ResponseAdapter<T, R> = (p: ResponseAdapterParams<T, R>) => InfiniteScrollResult<T>;

/* =========================================================================
 * 4. Функция загрузки данных (Fetcher)
 * ========================================================================= */
export type DataFetcher<T, R = any> = (
    page: number,
    pageSize: number,
    signal?: AbortSignal
) => Promise<R>;

/* =========================================================================
 * 5. Опции и внутреннее состояние хука бесконечной прокрутки
 * ========================================================================= */
export interface InfiniteScrollOptions<T, R = any> {
    pageSize?: number;
    threshold?: number;
    initialPage?: number;
    autoLoad?: boolean;
    resetDeps?: any[];
    responseAdapter: ResponseAdapter<T, R>;
    /** Дебаунс задержка в миллисекундах (по умолчанию 0 - отключен) */
    debounceWait?: number;
    /** Троттлинг задержка в миллисекундах (по умолчанию 0 - отключен) */
    throttleWait?: number;
    /** Сохранять предыдущие элементы при сбросе до получения новых данных */
    keepPreviousOnReset?: boolean;
}

export interface InfiniteScrollState<T> {
    items: T[];
    page: number;
    hasMore: boolean;
    total?: number;
    loading: boolean;
    isLoaded: boolean;
    error: Error | null;
}

/* =========================================================================
 * 6. ⚡️ Утилиты для «автоматического» выведения типов
 * ========================================================================= */
export type InferItem<RA> = RA extends ResponseAdapter<infer T, any> ? T : never;

export type InferResponse<RA> = RA extends ResponseAdapter<any, infer R> ? R : never;

/**
 * createAdapter ― helper для лаконичного объявления ResponseAdapter.
 *
 *  ▸ T  ― тип отдельного элемента (Item)
 *  ▸ R  ― тип «сырого» ответа, приходящего из fetchData
 *
 * TypeScript выводит оба типа автоматически из тела функции-адаптера,
 * поэтому при использовании дженерики писать не нужно.
 */
export function createAdapter<T, R>(fn: ResponseAdapter<T, R>): ResponseAdapter<T, R> {
    return fn;
}
